import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { NotableBreak } from '@shootout-comps/types';

interface NotableBreaksDialogProps {
  leagueId: string;
  leagueName: string;
  onClose: () => void;
}

function formatOccurredAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Owner/admin dialog listing every break recorded for this league (see League.notableBreakThreshold
// and POST/GET .../notable-breaks) — a read-only viewer; the threshold itself is configured on
// LeagueDashboardPage.
export default function NotableBreaksDialog({ leagueId, leagueName, onClose }: NotableBreaksDialogProps) {
  const { fetchNotableBreaks } = useLeague();

  const [breaks, setBreaks] = useState<NotableBreak[] | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    fetchNotableBreaks(leagueId)
      .then(setBreaks)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchNotableBreaks, leagueId]);

  useEffect(reload, [reload]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', animation: 'fadeInNotableBreaks 0.15s ease',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--baize), var(--felt))',
        border: '2px solid rgba(46,204,113,0.4)', borderRadius: '14px',
        padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '460px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUpNotableBreaks 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', lineHeight: 1, color: '#2ecc71' }}>
            🎯 Notable Breaks
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginBottom: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {leagueName}
        </p>

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: '#e74c3c', fontSize: '0.78rem', marginBottom: '0.9rem' }}>
            {error}
          </div>
        )}

        {breaks === null ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>…</p>
        ) : breaks.length === 0 ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', textAlign: 'center', padding: '1.5rem 0' }}>
            No notable breaks recorded yet. Set a threshold in the league's settings to start tracking them.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '360px', overflowY: 'auto' }}>
            {breaks.map(b => (
              <div key={b._id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.55rem 0.75rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.playerName}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginTop: '0.15rem' }}>{b.teamName} · {b.divisionName} · {formatOccurredAt(b.occurredAt)}</div>
                </div>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#2ecc71', flexShrink: 0 }}>{b.points}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeInNotableBreaks  { from{opacity:0} to{opacity:1} }
        @keyframes slideUpNotableBreaks { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
